import { Button, Form, Select, Upload } from 'antd';
import { FormInstance } from 'antd/lib/form';
import { UploadFile } from 'antd/lib/upload/interface';
import { UploadRequestOption, UploadRequestError } from 'rc-upload/lib/interface';
import React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { LoadingOutlined } from '@ant-design/icons';

import { BlockedByFeature } from '../../../featureToggles/features/utils';
import { ApiCredentials, IAccount, IIdentifier } from '../../../slices/accounts/account';
import { selectors as accountListSelectors } from '../../../slices/accounts/list';
import { selectors } from '../../../slices/apiCredentials';
import { ApiCredentialsFormState } from '../../../slices/apiCredentials/apiCredentialsFormState';
import { IApiCredentialsDTO, createApiCredentials, editApiCredentials } from '../../../slices/apiCredentials/form.thunks';
import { showErrorMessage } from '../../../slices/notifications';
import { RootState } from '../../../slices/rootReducer';
import { AccountsDashboard } from '../accountsDashboard';
import { ReactComponent as EmailSVG } from './../../../assets/sightx-icons/svg/icon-email.svg';
import { ReactComponent as KeySVG } from './../../../assets/sightx-icons/svg/icon-upload.svg';
import { IResendEmailApiCredentialsRequest, resendEmailApiCredentials } from './../../../slices/apiCredentials/resendEmailApiCredentials.thunks';
import { ListParams } from '../../../slices/utils';
import { FilterOperators, Ifilter } from '../../common/filters/interfaces';
import { actions as accountListActions } from '../../../slices/accounts/list';
import { isFeatureEnabled, Features } from '../../../featureToggles/features';

import styles from './apiKey.module.scss';

const isPlgExternal3Env = isFeatureEnabled(Features.PlgExternal3);

interface ApiKeyProps {
  accountId: string;
}

interface ApiKeyState extends IApiCredentialsDTO {
  showError: boolean;
  edited: boolean;
}

interface StateProps extends ApiCredentials {
  apiCredentialsId?: string;
  apiCredentialsForm: ApiCredentialsFormState;
  account: IAccount | typeof BlockedByFeature | null;
  accountId: string;
  loading: boolean;
}

interface DispatchProps {
  showErrorMessage: typeof showErrorMessage;
  createApiCredentials: (values: IApiCredentialsDTO) => Promise<unknown>;
  editApiCredentials: (values: IApiCredentialsDTO) => Promise<unknown>;
  resendEmailApiCredentials: (values: IResendEmailApiCredentialsRequest) => Promise<unknown>;
  fetchAccountsList: (values: ListParams<Ifilter[]>) => Promise<unknown>;
}

type Props = ApiKeyProps & StateProps & DispatchProps;

const validateMessages = {
  required: 'Required',
  types: {
    email: 'Invalid email format',
    regexp: 'Invalid'
  }
};

const defaultApiCredentials: ApiCredentials = {
  rsaKey: '',
  recipientsEmails: [],
  filename: '',
  clientId: ''
};

const fileTemplate: UploadFile = {
  size: 0,
  type: '',
  uid: '',
  name: ''
};

const oneMegabyteInBytes = 1024 * 1024;

class ApiKey extends React.Component<Props, ApiKeyState> {
  private formRef: React.RefObject<FormInstance>;
  constructor(props: Props) {
    super(props);
    this.state = {
      showError: false,
      rsaKey: props.rsaKey,
      recipientsEmails: props.recipientsEmails,
      filename: props.filename,
      accountId: props.accountId,
      edited: false
    };
    this.formRef = React.createRef<FormInstance>();
    this.handleSubmit = this.handleSubmit.bind(this);
    this.onChangeForm = this.onChangeForm.bind(this);
    this.clickResendEmailApiCredentials = this.clickResendEmailApiCredentials.bind(this);
    this.removeFile = this.removeFile.bind(this);
    this.validateFileExtensionSizeAndRead = this.validateFileExtensionSizeAndRead.bind(this);
  }

  componentDidMount(): void {
    if (!isPlgExternal3Env) {
      return;
    }
    const { accountId, fetchAccountsList } = this.props;
    const fetchAccountsRequest: ListParams<Ifilter[]> = {
      filters: [
        {
          field: '_id',
          operands: [accountId],
          operator: FilterOperators.isAnyOf,
          validateInputs: false
        }
      ],
      pagination: { offset: 0, limit: 25 }
    };
    fetchAccountsList(fetchAccountsRequest);
  }

  componentDidUpdate(prevProps: StateProps) {
    const { apiCredentialsForm } = this.props;
    if (prevProps.apiCredentialsForm.error !== apiCredentialsForm.error && apiCredentialsForm.error !== null) {
      this.setState({ showError: true });
    }
    if (prevProps.apiCredentialsForm.loading && !apiCredentialsForm.loading && apiCredentialsForm.error === null) {
      this.setState({ edited: false });
    }
  }

  private validateEmail(rule: unknown, value: string, callback: (msg?: string) => void) {
    const regexEmail = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    if (regexEmail.test(value)) {
      callback();
    } else {
      callback(`Invalid email format ${value}`);
    }
  }

  private renderResendButton(): React.ReactNode {
    const { apiCredentialsId } = this.props;
    const { edited } = this.state;
    if (apiCredentialsId) {
      return <Button
        className={styles.resendLink}
        type='link'
        onClick={this.clickResendEmailApiCredentials}
        disabled={edited}
      >
        <EmailSVG className={styles.emailIcon} />
        Resend registration email
      </Button>;
    }
    return null;
  }

  private onChangeForm() {
    if (!this.state.edited) {
      this.setState({ edited: true });
    }
  }

  private readFileContent(
    file: File,
    onSuccess: ((body: unknown, xhr: XMLHttpRequest) => void) | undefined,
    onError: ((event: UploadRequestError | ProgressEvent<EventTarget>, body?: unknown) => void) | undefined) {
    const pattern = new RegExp(/ssh-rsa AAAA[0-9A-Za-z+/]+[=]{0,3}/);
    const { showErrorMessage } = this.props;
    const currentForm = this.formRef.current;
    const reader = new FileReader();
    reader.onload = (event: ProgressEvent<FileReader>) => {
      if (typeof reader.result === 'string') {
        if (pattern.test(reader.result)) {
          this.setState({ rsaKey: reader.result, filename: file.name });
          onSuccess && onSuccess({}, new XMLHttpRequest());
          return;
        }
      }
      const message = 'Invalid public key';
      onError && onError({ name: 'Invalid public key', message });
      currentForm?.setFieldsValue({ upload: undefined, edited: true });
      showErrorMessage(message);
    };
    reader.readAsText(file);
  }

  private removeFile() {
    const currentForm = this.formRef.current;
    this.setState({ rsaKey: '', filename: '' });
    currentForm?.setFieldsValue({ upload: undefined, edited: true });
    currentForm?.validateFields();
  }

  private validateFileExtensionSizeAndRead(uploadRequestOption: UploadRequestOption) {
    const { file } = uploadRequestOption;
    const { showErrorMessage } = this.props;
    const currentForm = this.formRef.current;
    const fileExtension = file.name?.split('.').pop() || '';
    const onError = uploadRequestOption.onError;
    const onSuccess = uploadRequestOption.onSuccess;

    if (!['txt', 'pub'].includes(fileExtension)) {
      currentForm?.resetFields(['upload']);
      const message = 'You can only upload files with the extension .txt or .pub';
      showErrorMessage(message);
      onError && onError({ name: 'Incorrect file type', message });
      return;
    }

    if (file.size > oneMegabyteInBytes) {
      currentForm?.resetFields(['upload']);
      const message = 'File must be less than 1MB!';
      showErrorMessage(message);
      onError && onError({ name: 'File too big', message });
      return;
    }

    this.readFileContent(file, onSuccess, onError);
  }

  private async clickResendEmailApiCredentials(): Promise<void> {
    const { accountId } = this.props;
    await this.props.resendEmailApiCredentials({ accountId });
  }

  private renderEmailField(): React.ReactNode {
    const { recipientsEmails } = this.props;

    return <Form.Item
      name="recipientsEmails"
      label="Emails"
      rules={[{
        required: true,
        type: 'array',
        defaultField: { type: 'email', validator: this.validateEmail }
      }]}>
      <Select
        mode="tags"
        tokenSeparators={[',', ';']}
        placeholder="Emails should be separated by commas"
        onChange={this.onChangeForm}
        autoFocus={true}
        defaultValue={recipientsEmails}
      />
    </Form.Item>;
  }

  private async handleSubmit() {
    const currentForm = this.formRef.current;
    const { rsaKey, filename } = this.state;
    const rsaKeyValue = rsaKey ? rsaKey : this.props.rsaKey;
    const filenameValue = filename ? filename : this.props.filename;
    if (!filenameValue) {
      currentForm?.setFieldsValue({ upload: undefined });
      await currentForm?.validateFields();
      return;
    }
    await currentForm?.validateFields();
    const formValues = currentForm?.getFieldsValue();
    const { createApiCredentials, editApiCredentials, apiCredentialsId, accountId } = this.props;
    const values: IApiCredentialsDTO = {
      accountId,
      recipientsEmails: formValues.recipientsEmails,
      rsaKey: rsaKeyValue,
      filename: filenameValue
    };
    apiCredentialsId ? editApiCredentials(values) : createApiCredentials(values);
  }

  private renderFileField(): React.ReactNode {
    const { filename } = this.state;
    const filenameValue = !filename ? this.props.filename : filename;
    const file = filenameValue ? { ...fileTemplate, name: filenameValue } : undefined;

    return <Form.Item
      name="upload"
      label="File"
      rules={[{ required: true, message: 'Please enter a valid SSH key' }]}
    >
      <Upload
        {...(file ? { defaultFileList: [file] } : {})}
        accept=".txt,.pub"
        name="upload"
        maxCount={1}
        className={styles.upload}
        onChange={this.onChangeForm}
        onRemove={this.removeFile}
        customRequest={this.validateFileExtensionSizeAndRead}
      >
        <Button
          type="default"
          icon={<KeySVG className={styles.uploadIcon} />}
        >
          Upload public key file
        </Button>
      </Upload>
    </Form.Item>;
  }

  private renderError() {
    const { showError } = this.state;
    const { apiCredentialsForm } = this.props;
    if (!showError) {
      return null;
    }
    return (
      <div className={styles.errorMessage}>{apiCredentialsForm.error}</div>
    );
  }

  render(): React.ReactNode {
    if (this.props.loading) {
      return <AccountsDashboard
        actions={[
          <Button
            type="primary"
            className={styles.submitButton}
            disabled
          >
            Send
          </Button>
        ]}>
        <div className={styles.loadingContainer}>
          <LoadingOutlined />
        </div>
      </AccountsDashboard>;
    }

    const { account } = this.props;
    if (account === BlockedByFeature || account === null) {
      return null;
    }

    const { kmsKeyId, apiCredentialsId } = this.props;
    const { recipientsEmails, filename, edited } = this.state;
    const filenameValue = !filename ? this.props.filename : filename;
    const file = filenameValue ? { ...fileTemplate, name: filenameValue } : undefined;
    const recipientsEmailsValue = !recipientsEmails.length ? this.props.recipientsEmails : recipientsEmails;
    const initialValues = { recipientsEmails: recipientsEmailsValue, ...(file && { upload: [file] }) };

    return (
      <AccountsDashboard
        actions={[
          <Button
            type="primary"
            onClick={this.handleSubmit}
            className={styles.submitButton}
            disabled={!!apiCredentialsId && !edited}
          >
            Send
          </Button>
        ]}>
        <div className={styles.container} >
          <Form
            ref={this.formRef}
            colon={false}
            layout="vertical"
            validateMessages={validateMessages}
            initialValues={initialValues}
          >
            {kmsKeyId ?
              <div>A kms key has already been registered for this account</div> :
              <>
                {this.renderEmailField()}
                {this.renderFileField()}
                {this.renderError()}
              </>
            }
            {this.renderResendButton()}
          </Form>
        </div>
      </AccountsDashboard>
    );
  }
}

const mapStateToProps = (state: RootState, props: RouteComponentProps<IIdentifier>): StateProps => {
  const { id = '' } = props.match.params;
  const account = accountListSelectors.getAccountById(state, id);
  const apiCredentials = account?.apiCredentials || defaultApiCredentials;
  const apiCredentialsId = account?.apiCredentialsId || '';

  return {
    apiCredentialsId,
    ...apiCredentials,
    apiCredentialsForm: selectors.gettingStateUploadForm(state),
    account,
    accountId: id,
    loading: accountListSelectors.getLoading(state)
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<RootState, undefined, AnyAction>): DispatchProps => ({
  showErrorMessage,
  createApiCredentials: (values: IApiCredentialsDTO) => dispatch(createApiCredentials(values)),
  editApiCredentials: (values: IApiCredentialsDTO) => dispatch(editApiCredentials(values)),
  resendEmailApiCredentials: (values: IResendEmailApiCredentialsRequest) => dispatch(resendEmailApiCredentials(values)),
  fetchAccountsList: (values: ListParams<Ifilter[]>) => dispatch(accountListActions.fetchAccountsList(values))
});

const ConnectedApiKeySection = connect(mapStateToProps, mapDispatchToProps)(ApiKey);
export const ApiKeySection = withRouter(ConnectedApiKeySection);

